"use client";

import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { Globe, LinkIcon } from "lucide-react";

import { useProfileDataStore } from "@/features/profile/tutor/store/useProfileDataStore";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import availableLanguages from "@/i18n/availableLanguages";
import {
  settingsFormSchema,
  type SettingsFormValues,
} from "@/features/profile/tutor/schemas/settingsSchema.ts";
import { editTutorProfileSettings } from "@/features/profile/tutor/api/editProfile.ts";
import { getLanguageFromLocale } from "@/features/profile/utils/profileUtils.ts";
import envs from "@/envs.ts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { InformationTooltip } from "@/components/InformationTooltip";

type SettingsEditProps = {
  onSave: () => void;
  onCancel: () => void;
};

export function SettingsEdit({ onSave, onCancel }: SettingsEditProps) {
  const { settingsData: data, setToReload, profileData } = useProfileDataStore();
  const { t } = useTranslation(["profile", "common", "help"], {
    lng: getLanguageFromLocale(profileData?.header_section.locale_code),
  });

  const form = useForm<SettingsFormValues>({
    resolver: zodResolver(settingsFormSchema(t)),
    defaultValues: {
      main_profile: {
        main_profile_locale: data?.main_profile.main_profile_locale || "",
      },
      visibility: {
        enable_public_profile: data?.visibility.enable_public_profile || false,
        enable_workspace_join_via_url: data?.visibility.enable_workspace_join_via_url || false,
        profile_url_slug: data?.visibility.profile_url_slug || "",
        enable_public_chat_on_profile_page:
          data?.visibility.enable_public_chat_on_profile_page || false,
      },
    },
  });

  useEffect(() => {
    form.reset();
  }, [form]);

  const orderedLanguages = useMemo(() => {
    if (!data) return [];
    const mainLanguage = availableLanguages.find(
      (lang) => lang.language.toUpperCase() === data.main_profile.main_profile_locale,
    );
    const otherLanguages = data.main_profile.profiles_locales
      .filter((locale: string) => locale !== data.main_profile.main_profile_locale)
      .map((locale: string) =>
        availableLanguages.find((lang) => lang.language.toUpperCase() === locale),
      );

    return [mainLanguage, ...otherLanguages];
  }, [data?.main_profile.main_profile_locale, data?.main_profile.profiles_locales]);

  if (!data) return null;

  const handleSubmit = async (values: SettingsFormValues) => {
    const payload = {
      ...values,
      visibility: {
        enable_public_profile: Number(values.visibility.enable_public_profile),
        enable_workspace_join_via_url: Number(values.visibility.enable_workspace_join_via_url),
        profile_url_slug: values.visibility.profile_url_slug,
        enable_public_chat_on_profile_page: Number(
          values.visibility.enable_public_chat_on_profile_page,
        ),
      },
    };

    await editTutorProfileSettings(payload);
    setToReload(true);
    onSave();
  };

  return (
    <motion.div
      animate={{ opacity: 1, y: 0 }}
      initial={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.3 }}
    >
      <Form {...form}>
        <form className="flex h-full flex-col" onSubmit={form.handleSubmit(handleSubmit)}>
          <div className="space-y-6">
            {/* Visibility Settings */}
            <Card className="bg-side-accent/80 border-border/30 relative overflow-hidden">
              <div className="pointer-events-none absolute -top-12 -right-16 h-40 w-40 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600 opacity-10" />
              <div className="pointer-events-none absolute -bottom-10 -left-12 h-28 w-28 rounded-full bg-gradient-to-tr from-sky-400 to-cyan-500 opacity-10" />
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-accent-foreground text-lg">
                    {t("profile:settingsSection.visibilitySettings")}
                  </CardTitle>
                  <InformationTooltip
                    description={t("profile:settingsSection.visibilitySettingsTooltip")}
                  />
                </div>
              </CardHeader>
              <CardContent className={"relative z-50"}>
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="visibility.enable_public_profile"
                    render={({ field }) => (
                      <FormItem className="bg-muted hover:bg-muted flex flex-row items-start space-y-0 space-x-3 rounded-xl p-4 transition-colors">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            className="mt-1"
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel className="text-base font-medium">
                            {t("profile:settingsSection.enablePublicProfile")}
                          </FormLabel>
                          <FormDescription className="text-sm">
                            {t("profile:settingsSection.enablePublicProfileDescription")}
                          </FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="visibility.enable_public_chat_on_profile_page"
                    render={({ field }) => (
                      <FormItem className="bg-muted/75 hover:bg-muted flex flex-row items-start space-y-0 space-x-3 rounded-xl p-4 transition-colors">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            className="mt-1"
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel className="text-base font-medium">
                            {t("profile:settingsSection.enableChatOnProfile")}
                          </FormLabel>
                          <FormDescription className="text-sm">
                            {t("profile:settingsSection.enableChatOnProfileDescription")}
                          </FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="visibility.enable_workspace_join_via_url"
                    render={({ field }) => (
                      <FormItem className="bg-muted/75 hover:bg-muted flex flex-row items-start space-y-0 space-x-3 rounded-xl p-4 transition-colors">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            className="mt-1"
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel className="text-base font-medium">
                            {t("profile:settingsSection.enableWorkspaceJoinViaUrl")}
                          </FormLabel>
                          <FormDescription className="text-sm">
                            {t("profile:settingsSection.enableWorkspaceJoinViaUrlDescription")}
                          </FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Custom Profile URL */}
            <Card className="bg-side-accent/80 border-border/30 relative overflow-hidden">
              <div className="pointer-events-none absolute -top-12 -right-16 h-40 w-40 rounded-full bg-gradient-to-br from-indigo-400 to-purple-600 opacity-10" />
              <div className="pointer-events-none absolute -bottom-10 -left-12 h-28 w-28 rounded-full bg-gradient-to-tr from-purple-400 to-indigo-500 opacity-10" />
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <div className="bg-background/60 flex items-center rounded-xl p-2">
                    <LinkIcon className="h-4 w-4" />
                  </div>
                  <CardTitle className="text-accent-foreground text-lg">
                    {t("profile:settingsSection.customProfileUrl")}
                  </CardTitle>
                  <InformationTooltip
                    description={t("profile:settingsSection.customProfileUrlTooltip")}
                  />
                </div>
              </CardHeader>
              <CardContent className={"relative z-50"}>
                <FormField
                  control={form.control}
                  name="visibility.profile_url_slug"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <div className="bg-muted flex items-center gap-2 rounded-xl p-3">
                          <span className="text-muted-foreground text-sm whitespace-nowrap">
                            {`${envs.VITE_SITE_URL.replace("https://", "")}/profile/tutor/`}
                          </span>
                          <Input
                            className="flex-1 border-0 shadow-none focus-visible:ring-1"
                            placeholder="your-custom-url"
                            {...field}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            {/* Main Profile Language */}
            <Card className="bg-side-accent/80 border-border/30 relative overflow-hidden">
              <div className="pointer-events-none absolute -top-12 -right-16 h-40 w-40 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 opacity-10" />
              <div className="pointer-events-none absolute -bottom-10 -left-12 h-28 w-28 rounded-full bg-gradient-to-tr from-amber-400 to-pink-500 opacity-10" />
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <div className="bg-background/60 flex items-center rounded-xl p-2">
                    <Globe className="h-4 w-4" />
                  </div>
                  <CardTitle className="text-accent-foreground text-lg">
                    {t("profile:settingsSection.mainProfileLanguage")}
                  </CardTitle>
                  <InformationTooltip
                    description={t("profile:settingsSection.mainProfileLanguageTooltip")}
                  />
                </div>
              </CardHeader>
              <CardContent className={"relative z-50"}>
                <FormField
                  control={form.control}
                  name="main_profile.main_profile_locale"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <div
                          className={`grid-cols-${orderedLanguages.length > 3 ? 3 : orderedLanguages.length} grid gap-3`}
                        >
                          {orderedLanguages.map((lang, index) => {
                            const isSelected = field.value === lang.language.toUpperCase();

                            return (
                              <Button
                                key={`${lang.language}-${index}`}
                                className={`flex h-auto flex-col items-center justify-center gap-2 rounded-xl py-3 transition-all ${
                                  isSelected
                                    ? "bg-accent-foreground text-primary hover:bg-accent-foreground hover:text-background"
                                    : "bg-muted/75 text-muted-foreground hover:bg-muted"
                                }`}
                                type="button"
                                variant="ghostSecondary"
                                onClick={() => field.onChange(lang.language.toUpperCase())}
                              >
                                <div
                                  className={`rounded-md p-1 ${isSelected ? "bg-background/80" : "bg-muted/50"}`}
                                >
                                  <img
                                    alt={`${lang.name} flag`}
                                    className="h-8 w-10 rounded object-cover shadow-xs"
                                    src={lang.flagImg || "/placeholder.svg"}
                                  />
                                </div>
                                <span className="mt-1 text-xs font-medium">{lang.name}</span>
                              </Button>
                            );
                          })}
                        </div>
                      </FormControl>
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>
          </div>

          {/* Footer */}
          <div className="mb-8 flex justify-end gap-3 pt-6">
            <Button
              className="bg-muted/20 hover:bg-muted/30 border-border/50 rounded-xl"
              variant="outline"
              onClick={onCancel}
            >
              {t("common:cancel")}
            </Button>
            <Button
              className="bg-accent-foreground/90 hover:bg-accent-foreground text-background rounded-xl"
              type="submit"
            >
              {t("common:save")}
            </Button>
          </div>
        </form>
      </Form>
    </motion.div>
  );
}
